import { LEAGUE_DATABASE_VERSION } from "../../common/constants.ts";
import {
	gameAttributesCache,
	gameAttributesKeysOtherSports,
} from "../../common/defaultGameAttributes.ts";
import { gameAttributesArrayToObject } from "../../common/gameAttributesArrayToObject.ts";
import { idb } from "../db/index.ts";

// Serialize the currently open league to a gzipped league file Blob, matching
// the format of the normal export (ui/util/makeExportStream.ts) so it can be
// imported through the existing league file import path. Notably:
// - teamSeasons/teamStats are NOT top-level keys; they are embedded in each
//   team as `seasons`/`stats` (top-level copies would be double-imported and
//   blow up the (season, tid) unique index)
// - gameAttributes is an object with runtime-cache keys stripped
//
// Unlike makeExportStream this builds the whole JSON in memory, which is fine
// for the young leagues we snapshot in Phase 1. Revisit for Phase 7 snapshots
// of leagues with long histories.
export const exportSnapshotBlob = async (name: string) => {
	// Export must reflect the latest state
	await idb.cache.flush();

	const object: Record<string, unknown> = {
		version: LEAGUE_DATABASE_VERSION,
		meta: { name },
	};

	const teamSeasons = await idb.league.getAll("teamSeasons");
	const teamStats = await idb.league.getAll("teamStats");

	const storeNames = Array.from(idb.league.objectStoreNames);
	for (const store of storeNames) {
		if (store === "teamSeasons" || store === "teamStats") {
			continue;
		}

		if (store === "gameAttributes") {
			const rows = (await idb.league.getAll(store)).filter(
				(row) =>
					!gameAttributesCache.includes(row.key) &&
					!gameAttributesKeysOtherSports.has(row.key),
			);
			object[store] = gameAttributesArrayToObject(rows as any);
			continue;
		}

		const rows = await idb.league.getAll(store);

		if (store === "teams") {
			object[store] = rows.map((t: any) => ({
				...t,
				seasons: teamSeasons.filter((row: any) => row.tid === t.tid),
				stats: teamStats.filter((row: any) => row.tid === t.tid),
			}));
			continue;
		}

		object[store] = rows;
	}

	const json = JSON.stringify(object);
	const stream = new Blob([json])
		.stream()
		.pipeThrough(new CompressionStream("gzip"));
	return new Response(stream).blob();
};
